import { createBobWorldPicker } from './150-bob-world-picker.mjs';
import { createBobSequences } from './146-bob-sequences.mjs';
import { createBobSequenceView, paintBobResult, paintBobStart } from './147-bob-sequence-view.mjs';
import { bobFeedback, bobEffectText } from './142-bob-feedback.mjs';
export { bobFeedback, bobEffectText } from './142-bob-feedback.mjs';
import { createBobArcadeHud } from './144-bob-arcade-hud.mjs';
import { getBobWorld } from '../../../packages/domain/src/73-bob-worlds.mjs';
import { createBobPanels } from './86-bob-panels.mjs';
import { createBobInput } from './83-bob-input.mjs';
import { createBobOptions } from './85-bob-options.mjs';
import { paintIconAction } from './82-icon-actions.mjs';
import {
  createBobBounce,
  stepBobBounce,
  tapBobBounce,
  steerBobBounce,
  bobLandingCue,
  clearBobInput,
  recoverBobBounce,
} from '../../../packages/domain/src/68-bob-bounce.mjs';
import { getBobLevel } from '../../../packages/domain/src/67-bob-levels.mjs';
import { createBobRenderer, getBobBackdropUrl } from './74-bob-renderer.mjs';

const clamp = (n, low, high) => Math.max(low, Math.min(high, n));
let playerSerial = 0;

/** A private, explicitly started 3D round. The domain owns every collision and
 * award; this owner handles cancellable input, visibility and one rendering clock.
 * Only explicitly opted-in device records outlive the mounted card; no upload. */
export function createBobPlayer(
  ctx,
  {
    host,
    seed,
    allowed,
    visible,
    onProgress = () => {},
    onStart = () => {},
    rendererFactory = createBobRenderer,
  },
) {
  const root = ctx.e('section', 'bob-game');
  root.setAttribute('aria-label', 'Bob Bounce');
  const stage = ctx.e('div', 'bob-stage');
  function makeCanvas() {
    const canvas = ctx.e('canvas', 'bob-canvas');
    canvas.setAttribute('role', 'button');
    canvas.setAttribute('tabindex', '0');
    canvas.setAttribute(
      'aria-label',
      'Bounce Bob with Space or the Bounce button. Hold Left and Right or drag to steer. Classic taps are available in Settings.',
    );
    return canvas;
  }
  let canvas = makeCanvas();
  const safeView = ctx.e('div', 'bob-safe-view');
  safeView.setAttribute('aria-hidden', 'true');
  const hud = ctx.e('div', 'bob-hud');
  const levelLabel = ctx.e('span', 'bob-level');
  const score = ctx.e('strong', 'bob-score', '0');
  const time = ctx.e('span', 'bob-time');
  const pace = ctx.e('span', 'bob-pace');
  const effect = ctx.e('span', 'bob-effect');
  const motionStatus = ctx.e('div', 'bob-motion-status');
  motionStatus.append(pace, effect);
  const progress = ctx.e('progress', 'bob-distance');
  progress.max = 100;
  progress.value = 0;
  progress.setAttribute('aria-label', 'Course progress');
  hud.append(levelLabel, score, progress, time, motionStatus);
  const toast = ctx.e('div', 'bob-toast');
  toast.setAttribute('aria-hidden', 'true');
  const arcadeHud = createBobArcadeHud(ctx, stage);
  const overlay = ctx.e('div', 'bob-overlay');
  const eyebrow = ctx.e('span', 'bob-kicker', 'TWENTY WORLDS · ONE BOB');
  const title = ctx.e('h3', '', 'Ready. Set. Bounce.');
  const description = ctx.e(
    'p',
    '',
    `Hold arrows or drag to steer. Bounce near landing. ${getBobWorld(1).hint}`,
  );
  const play = ctx.e('button', 'game-action bob-play');
  paintBobStart(ctx, play, 'Meet Bob');
  play.type = 'button';
  const results = ctx.e('div', 'bob-results');
  results.hidden = true;
  const artStatus = ctx.e('span', 'bob-art-status');
  artStatus.hidden = true;
  overlay.append(eyebrow, title, description, artStatus, results, play);
  const hint = ctx.e('span', 'bob-hint', 'Tap Bob to bounce');
  hint.id = `bob-timing-${++playerSerial}`;
  canvas.setAttribute('aria-describedby', hint.id);
  const pause = ctx.e('button', 'game-action bob-pause', 'Pause');
  pause.type = 'button';
  paintIconAction(ctx, pause, 'pause', 'Pause this round');
  const select = createBobWorldPicker(ctx, { onChange: () => chooseLevel() });
  overlay.append(select.root);
  const live = ctx.e('p', 'bob-live');
  live.setAttribute('role', 'status');
  stage.append(canvas, safeView, hud, toast, hint, overlay);
  root.append(stage, live);
  host.append(root);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sequences = createBobSequences({
    onChange: () => {
      if (sequences.snapshot().phase === 'error') {
        renderer?.dispose();
        graphicsFailure('assets');
      } else refresh();
    },
  });
  const sequenceView = createBobSequenceView(ctx, stage, () => {
    sequences.skip();
    refresh();
  });
  let scripted = false;
  let state = createBobBounce(1, seed);
  let renderer = null,
    pending = null,
    generation = 0,
    intent = 0,
    graphicsAttempts = 0;
  let disposed = false,
    started = false,
    paused = false,
    frame = 0,
    previous = 0;
  let input = null;
  let bufferedUntil = null;
  let toastUntil = 0,
    toastSource = null,
    lastEvent = 0,
    celebrationUntil = 0;
  let resultReveal = null;
  let previewLevel = null;
  let inputEvents = [];
  const canRun = () => !disposed && allowed() && visible() && !document.hidden;
  const playable = () =>
    canRun() &&
    started &&
    !paused &&
    renderer &&
    sequences.snapshot().phase === 'playing' &&
    !['failed', 'complete'].includes(state.status);
  const course = () => getBobLevel(state.level);
  function report() {
    onProgress({
      score: state.score,
      stage: state.level,
      completed: state.status === 'complete',
    });
  }
  function paint() {
    input?.paint();
    const level = course();
    const sequence = sequences.snapshot();
    sequenceView.paint({ ...sequence, active: sequence.active && !paused }, state, level);
    stage.dataset.cinematic = String(sequence.active && !paused);
    if (previewLevel !== state.level) {
      previewLevel = state.level;
      stage.style.backgroundImage = `url("${getBobBackdropUrl(state.level)}")`;
    }
    const cue = bobLandingCue(state);
    levelLabel.textContent = `${state.level} / 20 · ${level.name}`;
    score.textContent = state.score.toLocaleString();
    arcadeHud.paint(state, Boolean(playable()));
    const speed = Math.hypot(state.bob.vx, state.bob.vz).toFixed(1);
    pace.textContent = `${speed} m/s`;
    pace.setAttribute('aria-label', `Speed ${speed} metres per second`);
    stage.dataset.boosted = String(state.effects.boostUntil > state.elapsed);
    effect.dataset.active = String(Boolean(bobEffectText(state)));
    const seconds = Math.max(0, Math.ceil((level.timeLimitMs - state.elapsed) / 1000));
    time.textContent = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')} · World ${state.level}`;
    progress.value = clamp((state.bob.z / level.finish.z) * 100, 0, 100);
    canvas.setAttribute('aria-disabled', String(!playable()));
    pause.disabled = !started || !canRun() || ['failed', 'complete'].includes(state.status);
    paintIconAction(
      ctx,
      pause,
      paused ? 'play' : 'pause',
      paused ? 'Resume this round' : 'Pause this round',
    );
    stage.dataset.running = String(Boolean(playable()));
    panels.setPlaying(Boolean(playable()) || Boolean(resultReveal) || (sequence.active && !paused));
    stage.dataset.celebrating = String(Boolean(resultReveal));
    stage.dataset.phase = state.status;
    stage.dataset.timing = cue.window;
    const lane = level.bonusRoutes.find(
      (route) => route.z > state.bob.z && route.z - state.bob.z < 22,
    );
    hint.hidden = state.status !== 'ready' && (!lane || state.status !== 'playing' || paused);
    const reward =
      lane &&
      level.crates
        .filter((crate) => crate.routeId === lane.id)
        .reduce((sum, crate) => sum + crate.points, lane.reward);
    hint.textContent =
      state.status === 'ready'
        ? 'Hold arrows to steer · Bounce near landing'
        : lane
          ? `${lane.x > state.bob.x ? '←' : '→'} ${lane.name || 'Spring route'} · ${reward.toLocaleString()} base points + chains`
          : '';

    effect.textContent = [
      bobEffectText(state),
      state.earnings.collection
        ? `Goodies ${state.earnings.collection}`
        : 'No goodies · next hit ends run',
    ]
      .filter(Boolean)
      .join(' · ');
  }
  function stopClock() {
    cancelAnimationFrame(frame);
    frame = 0;
    previous = 0;
  }
  function clearInput() {
    input?.clear();
    bufferedUntil = null;
    state = clearBobInput(state);
    inputEvents = [];
    settings.audio.stop();
  }

  function resize() {
    if (!renderer || !canRun()) return;
    // The full canvas continues behind chrome; the safe rectangle anchors the
    // camera's central play projection without clipping surrounding scenery.
    const bounds = canvas.getBoundingClientRect();
    const scene = safeView.getBoundingClientRect();
    renderer.resize?.(bounds.width, bounds.height, Math.min(window.devicePixelRatio || 1, 1.5), {
      width: scene.width,
      height: scene.height,
      left: scene.left - bounds.left,
      top: scene.top - bounds.top,
    });
    renderer.draw(state, 0, reduced.matches, sequences.snapshot().presentation);
  }
  function graphicsFailure(reason) {
    sequences.cancel();
    renderer = null;
    paused = true;
    clearInput();
    stopClock();
    if (disposed) return;
    overlay.hidden = false;
    title.textContent =
      reason === 'replaced'
        ? 'Bob is taking a breather'
        : started
          ? 'Graphics paused'
          : 'The 3D scene could not load';
    description.textContent =
      reason === 'replaced'
        ? 'Another game is using the 3D renderer. Resume whenever you are ready.'
        : started
          ? 'Your run is saved here. Reload the scene to continue.'
          : 'Try loading the scene again. This game needs WebGL 2 graphics.';
    paintBobStart(ctx, play, started ? 'Reload scene' : 'Try again');
    play.disabled = false;
    paint();
  }
  async function loadGraphics() {
    if (renderer) return true;
    if (pending) return pending;
    const epoch = ++generation;
    // A released WebGL lease force-loses its context. A new renderer cannot
    // recover by attaching to that same canvas, so retry owns a fresh surface.
    if (graphicsAttempts++) {
      clearInput();
      resizeObserver?.unobserve(canvas);
      for (let i = listeners.length - 1; i >= 0; i--) {
        const [node, name, fn] = listeners[i];
        if (node !== canvas) continue;
        node.removeEventListener(name, fn);
        listeners.splice(i, 1);
      }
      const oldCanvas = canvas;
      canvas = makeCanvas();
      canvas.setAttribute('aria-describedby', hint.id);
      oldCanvas.replaceWith(canvas);
      bindCanvas();
      resizeObserver?.observe(canvas);
    }
    let unavailable = false;
    const fail = (reason) => {
      if (disposed || epoch !== generation || unavailable) return;
      unavailable = true;
      graphicsFailure(reason);
    };
    play.disabled = true;
    paintBobStart(ctx, play, 'Opening the world…');
    pending = (async () => {
      try {
        const lease = await rendererFactory({
          canvas,
          canInitialize: () => epoch === generation && canRun(),
          onUnavailable: fail,
          onArtStatus: (status) => {
            if (disposed || epoch !== generation) return;
            artStatus.hidden = status === 'ready';
            artStatus.textContent =
              status === 'loading' ? 'Adding Bob’s new artwork…' : 'Classic Bob ready';
          },
          reducedMotion: reduced.matches,
          onInvalidate: () => {
            if (!unavailable && epoch === generation) resize();
          },
        });
        if (disposed || epoch !== generation || unavailable || !allowed() || !visible()) {
          lease?.dispose();
          return false;
        }
        if (!lease) {
          fail('unavailable');
          return false;
        }
        renderer = lease;
        resize();
        return renderer === lease;
      } catch {
        fail('load');
        return false;
      } finally {
        if (epoch === generation) {
          pending = null;
          play.disabled = false;
          if (!renderer && !started) paintBobStart(ctx, play, 'Try again');
        }
      }
    })();
    return pending;
  }
  function finish(returnFocus) {
    input?.clear();
    bufferedUntil = null;
    const result = settings.finish(state);
    select.unlock(settings.unlockedLevel);
    if (scripted) sequences.finish(state.status === 'complete', reduced.matches);
    const celebrating = state.status === 'complete' && !reduced.matches;
    resultReveal = celebrating ? { focus: returnFocus } : null;
    restart.hidden = false;
    overlay.hidden = celebrating;
    paintBobResult({ ctx, state, result, eyebrow, title, description, results, play });
    live.textContent = `${title.textContent} ${state.score} local points.`;
    if (returnFocus && !celebrating) play.focus?.({ preventScroll: true });
    paint();
    report();
  }
  function tick(now) {
    frame = 0;
    if (!canRun() || paused || !renderer) {
      previous = 0;
      return;
    }
    const dt = previous ? clamp(now - previous, 0, 50) : 0;
    previous = now;
    const sequence = sequences.update(dt, reduced.matches);
    if (sequence.go && state.status === 'ready') {
      state = tapBobBounce(state, { aimZ: 1 });
      inputEvents.push(...state.events);
      toast.textContent = 'GO!';
      toastSource = null;
      Object.assign(toast.style, {
        insetInline: '',
        left: '',
        right: '',
        top: '',
        marginInline: '',
      });
      toastUntil = now + 650;
      live.textContent = 'Go! Hold arrows or drag to steer. Bounce near landing.';
    }
    const before = state.status;
    if (sequence.phase === 'playing' && started && !['failed', 'complete'].includes(before)) {
      const cue = bobLandingCue(state);
      if (
        settings.assist &&
        state.status === 'playing' &&
        state.bob.vy < 0 &&
        cue.ms <= 120 &&
        cue.canTap
      ) {
        state.assisted = true;
        tap();
      } else if (bufferedUntil !== null && state.elapsed <= bufferedUntil && cue.canTap) tap();
      if (bufferedUntil !== null && state.elapsed > bufferedUntil) bufferedUntil = null;
      state = stepBobBounce(state, dt);
    }
    if (inputEvents.length) {
      state = {
        ...state,
        events: [
          ...new Map([...inputEvents, ...state.events].map((event) => [event.id, event])).values(),
        ],
      };
      inputEvents = [];
    }
    settings.audio.playEvents((state.events || []).filter((event) => event.id > lastEvent));
    let feedbackPriority = -1;
    for (const event of state.events || []) {
      if (event.id <= lastEvent) continue;
      lastEvent = event.id;
      arcadeHud.event(event);
      const feedback = bobFeedback(event);
      if (feedback && feedback.priority >= feedbackPriority) {
        feedbackPriority = feedback.priority;
        toast.textContent = feedback.text;
        toast.dataset.kind = event.type;
        toast.dataset.beat = String(event.id % 2);
        toastUntil = now + feedback.duration;
        toastSource = {
          x: event.x ?? state.bob.x,
          y: event.y ?? state.bob.y,
          z: event.z ?? state.bob.z,
        };
        if (feedback.announcement) live.textContent = feedback.announcement;
      }
    }
    if (resultReveal && (scripted ? sequence.phase === 'results' : now >= celebrationUntil)) {
      overlay.hidden = false;
      if (resultReveal.focus) play.focus?.({ preventScroll: true });
      resultReveal = null;
    }
    toast.hidden = now >= toastUntil;
    const returnFocus = [canvas, pause].includes(document.activeElement);
    paint();
    renderer.draw(state, dt / 1000, reduced.matches, sequence.presentation);
    if (toastSource && !toast.hidden) {
      const point = renderer.projectEvent?.(toastSource),
        bounds = stage.getBoundingClientRect();
      toast.hidden = !point;
      if (point) {
        const width = toast.offsetWidth || 0,
          height = toast.offsetHeight || 0;
        Object.assign(toast.style, {
          insetInline: 'auto',
          right: 'auto',
          marginInline: '0',
          left: `${Math.max(8, Math.min(bounds.width - width - 8, point.x - bounds.left - width / 2))}px`,
          top: `${Math.max(8, Math.min(bounds.height - height - 8, point.y - bounds.top - height))}px`,
        });
      }
    }
    if (state.status !== before && ['failed', 'complete'].includes(state.status)) {
      celebrationUntil = reduced.matches ? now : now + 1200;
      finish(returnFocus);
    }
    if (playable() || sequences.snapshot().active || now < Math.max(celebrationUntil, toastUntil))
      frame = requestAnimationFrame(tick);
    else settings.audio.suspend();
  }
  function refresh() {
    if (disposed) return;
    if (resultReveal && sequences.snapshot().phase === 'results') {
      overlay.hidden = false;
      if (resultReveal.focus && canRun()) play.focus?.({ preventScroll: true });
      resultReveal = null;
    }
    if (!canRun()) {
      if (resultReveal) {
        resultReveal = null;
        sequences.finish(false, true);
        overlay.hidden = false;
      }
      intent++;
      clearInput();
      settings.audio.suspend();
      stopClock();
      if (started) paused = true;
      paint();
      return;
    }
    paint();
    if (paused && started && !['failed', 'complete'].includes(state.status)) {
      overlay.hidden = false;
      title.textContent = 'Ready when you are';
      description.textContent = `Paused · ${getBobWorld(state.level).name}. ${getBobWorld(state.level).hint}`;
      paintBobStart(ctx, play, 'Resume');
    }
    resize();
    if (!paused && (playable() || sequences.snapshot().active) && !frame)
      frame = requestAnimationFrame(tick);
  }
  async function begin() {
    if (!canRun()) return;
    settings.resumeAudio();
    const retainPauseFocus = paused && document.activeElement === pause;
    const resuming = paused && started && !['failed', 'complete'].includes(state.status);
    const nextWorld = state.status === 'complete';
    const request = ++intent;
    onStart();
    if (!(await loadGraphics()) || !canRun() || request !== intent) return;
    if (['failed', 'complete'].includes(state.status)) {
      const next =
        state.status === 'complete'
          ? Math.min((state.level % 20) + 1, settings.unlockedLevel)
          : state.level;
      state = state.status === 'failed' ? recoverBobBounce(state) : createBobBounce(next, seed);
      lastEvent = 0;
      toastUntil = 0;
      toast.hidden = true;
      select.value = String(next);
    }
    restart.hidden = true;
    results.hidden = true;
    eyebrow.textContent = 'TWENTY WORLDS · ONE BOB';
    if (settings.assist) state.assisted = true;
    settings.paint(state.level);
    started = true;
    paused = false;
    overlay.hidden = true;
    panels.close();
    select.close();
    paintIconAction(ctx, pause, 'pause', 'Pause this round');
    previous = 0;
    if (!resuming || sequences.snapshot().phase === 'idle') {
      scripted = typeof renderer.prepare === 'function';
      sequences.start(scripted ? renderer.prepare(state) : null, {
        reduced: reduced.matches,
        next: nextWorld,
      });
    }
    live.textContent = scripted
      ? 'Getting Bob and the world ready.'
      : 'Press Bounce to begin. Hold arrows or drag to steer.';
    report();
    refresh();
    if (!retainPauseFocus) canvas.focus?.({ preventScroll: true });
  }
  function pauseRound() {
    intent++;
    if (!started) return;
    paused = true;
    clearInput();
    settings.audio.suspend();
    stopClock();
    refresh();
  }
  function tap(aimX = 0) {
    if (!playable()) return;
    settings.audio.resume();
    if (!bobLandingCue(state).canTap) {
      bufferedUntil = state.elapsed + 120;
      return;
    }
    bufferedUntil = null;
    if (settings.mode === 'classic' && aimX !== 0) state = steerBobBounce(state, null);
    state = tapBobBounce(state, { aimX, aimZ: 1 });
    inputEvents.push(...state.events);
    renderer.draw(state, 0, reduced.matches, sequences.snapshot().presentation);
    paint();
  }
  function chooseLevel() {
    const chosen = Number(select.value);
    if (!Number.isInteger(chosen) || chosen < 1 || chosen > settings.unlockedLevel) {
      select.value = String(state.level);
      return;
    }
    intent++;
    clearInput();
    stopClock();
    state = createBobBounce(Number(select.value), seed);
    sequences.cancel();
    resultReveal = null;
    lastEvent = 0;
    toastUntil = 0;
    toast.hidden = true;
    started = false;
    paused = false;
    overlay.hidden = false;
    title.textContent = course().name;
    eyebrow.textContent = `WORLD ${state.level} · ${getBobWorld(state.level).name}`;
    description.textContent = `Hold arrows to steer. Bounce near landing. ${getBobWorld(state.level).hint}`;
    paintBobStart(ctx, play, 'Play this world');
    results.hidden = true;
    restart.hidden = true;
    settings.paint(state.level);
    report();
    refresh();
  }
  function cancel() {
    clearInput();
    refresh();
  }
  const listeners = [];
  const listen = (node, name, fn, options) => {
    node.addEventListener(name, fn, options);
    listeners.push([node, name, fn]);
  };
  const panels = createBobPanels(ctx, { stage, pause, listen, onOpen: pauseRound });
  const settings = createBobOptions(ctx, panels.options, {
    sound: panels.sound,
    recordsHost: panels.records,
    onControls: () => clearInput(),
    onUnlocks: (level) => select.unlock(level),
    onAssist: (value) => {
      if (value) state.assisted = true;
    },
  });
  select.unlock(settings.unlockedLevel);
  const restart = ctx.e('button', 'game-action bob-restart', 'Restart for a clean run');
  restart.type = 'button';
  restart.hidden = true;
  overlay.append(restart);
  listen(restart, 'click', () => {
    select.value = String(state.level);
    chooseLevel();
    void begin();
  });
  function bindCanvas() {
    const node = canvas;
    input.bind(node);
    // Safari can start its text callout from touch defaults even when the
    // matching pointerdown was cancelled. Only the live canvas owns gestures;
    // native course controls, paused scrolling and assistive clicks remain free.
    const suppressGesture = (event) => {
      if (playable() && event.cancelable !== false) event.preventDefault();
    };
    for (const type of ['touchstart', 'touchmove'])
      listen(node, type, suppressGesture, { passive: false });
    for (const type of ['contextmenu', 'selectstart', 'dragstart', 'dblclick'])
      listen(node, type, suppressGesture);
  }
  for (const type of ['selectstart', 'contextmenu', 'dragstart'])
    listen(root, type, (event) => {
      if (event.target.closest?.('select, option, input, textarea, button, summary')) return;
      if (event.cancelable !== false) event.preventDefault();
    });
  input = createBobInput({
    ctx,
    stage,
    root,
    listen,
    playable,
    tap,
    steer: (x) => {
      state = steerBobBounce(state, x);
    },
    getCanvas: () => canvas,
    getMode: () => settings.mode,
    getProjection: () => renderer?.projectBob?.(),
  });
  bindCanvas();
  listen(root, 'focusout', () => {
    input.clear();
  });
  listen(play, 'click', begin);
  listen(pause, 'click', () => (paused ? begin() : pauseRound()));
  listen(document, 'visibilitychange', refresh);
  listen(window, 'blur', pauseRound);
  reduced.addEventListener?.('change', refresh);
  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
  resizeObserver?.observe(canvas);
  resizeObserver?.observe(safeView);
  paint();
  return {
    refresh,
    pause: pauseRound,
    cancel,
    dispose() {
      if (disposed) return;
      disposed = true;
      generation++;
      intent++;
      clearInput();
      stopClock();
      sequences.cancel();
      sequenceView.dispose();
      select.dispose();
      settings.dispose();
      renderer?.dispose();
      renderer = null;
      resizeObserver?.disconnect();
      reduced.removeEventListener?.('change', refresh);
      for (const [node, name, fn] of listeners) node.removeEventListener(name, fn);
      root.remove();
    },
  };
}
